<svelte:options runes={false} />
<script>
  // Decisions tab (TASK-20260728-researcher-observability-ui) — the researcher-facing decision lineage
  // surface: a frozen-version trust card, the multi-lane decision tape (NAV · regime · decisions · alerts ·
  // steering on ONE time axis), a causality inspector, the §10 exits-by-rule drift panel, and a filterable
  // decision blotter. Data: the membership-gated lineage reads (decisions ⋈ outcomes, steering audit,
  // version) + the existing alert/adjustment feeds. LIVE half: NAV-tick hint-then-refetch + a 30 s fallback
  // poll while Running — identical wiring to CandlesSignalsTab (KTD7).
  //
  // Honest empty states: a run with no captured lineage (non-versioned strategy) says exactly that — the
  // steering + alert lanes still render (they exist for every run); nothing is faked.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq, decisionTickSeq } from '../../../lib/autotradeStore.js';
  import {
    EXIT_RULES, exitRuleMeta, decisionMeta, mergeSteeringFeed, exitRuleDistribution, formatBps, formatNum,
  } from '../../../lib/lineage.js';
  import DecisionTapeChart from '../../shared/charts/DecisionTapeChart.svelte';
  import DecisionInspector from '../DecisionInspector.svelte';

  export let strategy = null;
  export let status = null;
  export let result = null; // StrategyPerformanceResult (host-owned) — supplies the NAV context lane

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  // ---- Data state ----
  let decisions = [];
  let steeringFeed = [];
  let alerts = []; // merged errors+alerts (all severities) for the alert lane
  let version = null; // LineageVersionResult
  let loading = false;
  let error = null;
  let inFlight = false;

  // ---- Filters (client-side on the fetched window; server filters exist but one fetch serves all panels) ----
  let filterType = '';
  let filterSymbol = '';
  let filterRule = '';
  let selectedDecision = null;
  let blotterLimit = 100;

  async function loadAll() {
    if (!strategyId) return;
    if (inFlight) return;
    inFlight = true;
    loading = true;
    const at = $selectedOrgId;
    const sid = strategyId;
    try {
      const [dec, steer, adj, ver, errRows, alertRows] = await Promise.all([
        api.getStrategyDecisions(sid, {}, at),
        api.getSteeringEvents(sid).catch(() => []),
        api.getAdjustments(sid, at).catch(() => []),
        api.getLineageVersion(sid, at).catch(() => null),
        api.getStrategyErrors(sid, at).catch(() => []),
        api.getStrategyAlerts(sid, at).catch(() => []),
      ]);
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return; // stale response — drop
      decisions = Array.isArray(dec) ? dec : [];
      steeringFeed = mergeSteeringFeed(steer, adj);
      version = ver;
      alerts = [...(errRows ?? []), ...(alertRows ?? [])]
        .map((r) => ({ time: r.createdAt, severity: r.severity, code: r.code, seq: r.seq }))
        .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
      error = null;
      // Keep the selected decision fresh across refetches (same id → the refetched row).
      if (selectedDecision) {
        selectedDecision = decisions.find((d) => d.id === selectedDecision.id) ?? selectedDecision;
      }
    } catch (err) {
      const msg = String(err?.message ?? err);
      error = msg.includes('status: 404')
        ? null // unreadable/no data → the empty state below, not an error banner
        : (msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg);
    } finally {
      loading = false;
      inFlight = false;
    }
  }

  // Reload on (strategy, org) change. Seed the guard from the underlying stores, never from a `$:` value
  // (lesson 2026-07-14 — reactive declarations compute after the instance script; a `$:`-seeded guard fires
  // a duplicate initial fetch).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      decisions = [];
      steeringFeed = [];
      alerts = [];
      version = null;
      selectedDecision = null;
      loadAll();
    }
  }

  // LIVE: refetch on each NAV-tick hint (decisions are composed on the same live tick that writes the bar).
  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadAll();
  }

  // …and on the dedicated decision watermark (this task) — a precise hint when a real decision lands, ahead of
  // the NAV cadence. Both coalesce in loadAll's inFlight guard. Seeded from a raw var (lesson 2026-07-14).
  let lastDecisionSeq = 0;
  $: if (isRunning && $decisionTickSeq !== lastDecisionSeq) {
    lastDecisionSeq = $decisionTickSeq;
    loadAll();
  }

  // Fallback liveness poll (mirrors CandlesSignalsTab).
  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadAll, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });

  onMount(loadAll);

  // ---- Derived views ----
  $: hasLineage = decisions.length > 0;
  $: lineageAvailable = version?.available ?? false;

  // The regime lane: regime score sampled from every snapshot that carries it, oldest→newest, deduped by bar.
  $: regimePoints = (() => {
    const byTime = new Map();
    for (const d of decisions) {
      const v = d.signalSnapshot?.regime ?? d.signalSnapshot?.score_regime;
      if (v == null) continue;
      byTime.set(d.barTime, Number(v));
    }
    return [...byTime.entries()]
      .map(([time, value]) => ({ time, value }))
      .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
  })();

  $: navPoints = Array.isArray(result?.equityCurve)
    ? result.equityCurve.map((p) => ({ time: p.time, value: p.value }))
    : [];

  $: filtered = decisions.filter((d) =>
    (!filterType || d.decisionType === filterType)
    && (!filterSymbol || d.symbol === filterSymbol)
    && (filterRule === '' || (d.decisionType === 'exit' && String(d.exitRule) === filterRule)));

  // Blotter symbols come from the DATA (canonical form) so the filter always matches real rows.
  $: dataSymbols = [...new Set(decisions.map((d) => d.symbol).filter(Boolean))].sort();

  $: distribution = exitRuleDistribution(decisions);
  $: maxRuleCount = Math.max(1, ...EXIT_RULES.map((r) => distribution.byRule.get(r.id) ?? 0));

  function selectFromTape(e) {
    if (e.detail.kind === 'decision') selectedDecision = e.detail.item;
  }
  function weightDelta(d) {
    const pre = d.signalSnapshot?.weight_pre;
    const post = d.signalSnapshot?.weight_post;
    if (pre == null && post == null) return '—';
    return `${pre != null ? formatNum(pre, 3) : '—'} → ${post != null ? formatNum(post, 3) : '—'}`;
  }
  function primaryScore(d) {
    const s = d.signalSnapshot ?? {};
    if (d.decisionType === 'entry') return s.entry_score != null ? formatNum(s.entry_score, 3) : '—';
    return s.exit_score != null ? formatNum(s.exit_score, 3) : '—';
  }
  function firstSlippage(d) {
    const o = (d.outcomes ?? []).find((x) => x.slippageBps != null);
    return o ? formatBps(o.slippageBps) : '—';
  }
  function outcomeStatus(d) {
    const o = d.outcomes ?? [];
    if (o.length === 0) return d.decisionType === 'book_snapshot' ? '' : 'not placed';
    return o.map((x) => x.status).join(', ');
  }
  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }
</script>

<div class="dt-root">
  <!-- Trust card: the frozen version identity behind every decision below. -->
  {#if lineageAvailable && version?.version}
    <div class="dt-version" role="note" aria-label="Strategy version">
      <span class="dt-v-chip">FROZEN</span>
      <span class="dt-v-id">{version.version.strategyId}</span>
      <span class="dt-v-ver">v{version.version.version}</span>
      <span class="dt-v-commit" title="Research/audit commit">code {String(version.version.codeCommit).slice(0, 10)}</span>
      <span class="dt-v-commit" title="K2 engine build">engine {String(version.version.engineCommit).slice(0, 10)}</span>
      {#if version.version.evidenceRef}
        <span class="dt-v-evidence" title="Supporting evidence">{version.version.evidenceRef}</span>
      {/if}
      <details class="dt-v-params">
        <summary>frozen parameters</summary>
        <pre>{JSON.stringify(version.version.params ?? {}, null, 2)}</pre>
      </details>
    </div>
  {/if}

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll}>Retry</button>
    </div>
  {/if}

  <div class="dt-toolbar">
    {#if isRunning}
      <span class="dt-live" title="Updating live on each NAV tick">● live</span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll} disabled={loading}>Refresh</button>
  </div>

  {#if loading && !hasLineage && steeringFeed.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if !hasLineage && !lineageAvailable}
    <!-- Honest empty state: lineage capture is opt-in per strategy version. Steering/alerts still shown. -->
    <div class="empty-state dt-empty">
      <p class="empty-state-description">
        <strong>This run has no captured decision lineage.</strong>
        Decision-level capture requires a versioned strategy (currently Shishito and KCP-1); this strategy either
        predates lineage capture or does not implement the version descriptor. Steering and alert history
        below is still complete.
      </p>
    </div>
    {#if steeringFeed.length > 0 || alerts.length > 0}
      <DecisionTapeChart
        {navPoints}
        regimePoints={[]}
        decisions={[]}
        {alerts}
        steering={steeringFeed}
        on:select={selectFromTape} />
    {/if}
  {:else}
    <div class="dt-tape-grid">
      <div class="dt-tape-main">
        <DecisionTapeChart
          {navPoints}
          {regimePoints}
          decisions={filtered}
          {alerts}
          steering={steeringFeed}
          selectedId={selectedDecision?.id ?? null}
          on:select={selectFromTape} />
      </div>
      <div class="dt-tape-side">
        <DecisionInspector decision={selectedDecision} on:close={() => (selectedDecision = null)} />
      </div>
    </div>

    <!-- §10 drift panel: exits by rule — distribution shifts are an early drift signal. -->
    <div class="dt-dist" aria-label="Exits by rule">
      <h4 class="dt-panel-title">Exits by rule <span class="dt-dist-total">({distribution.total} exits — distribution shifts are an early drift signal)</span></h4>
      <div class="dt-dist-rows">
        {#each EXIT_RULES as r (r.id)}
          {@const count = distribution.byRule.get(r.id) ?? 0}
          <button
            type="button"
            class="dt-dist-row"
            class:selected={filterRule === String(r.id)}
            title={r.detail}
            on:click={() => (filterRule = filterRule === String(r.id) ? '' : String(r.id))}>
            <span class="dt-dist-label">EXIT {r.id} · {r.label}</span>
            <span class="dt-dist-bar-track">
              <span class="dt-dist-bar" style={`width:${(count / maxRuleCount) * 100}%;background:${r.color}`}></span>
            </span>
            <span class="dt-dist-count num">{count}</span>
          </button>
        {/each}
      </div>
    </div>

    <!-- Blotter: every decision, filterable, newest-first. -->
    <div class="dt-blotter">
      <div class="dt-blotter-head">
        <h4 class="dt-panel-title">Decision blotter</h4>
        <div class="dt-filters">
          <select bind:value={filterType} aria-label="Filter by decision type">
            <option value="">All types</option>
            <option value="entry">Entries</option>
            <option value="exit">Exits</option>
            <option value="external_close">Framework closes</option>
            <option value="book_snapshot">Book snapshots</option>
          </select>
          <select bind:value={filterSymbol} aria-label="Filter by symbol">
            <option value="">All symbols</option>
            {#each dataSymbols as s (s)}
              <option value={s}>{s.replace('_', '-')}</option>
            {/each}
          </select>
          {#if filterRule !== ''}
            <button type="button" class="btn btn-ghost btn-sm" on:click={() => (filterRule = '')}>
              Clear rule filter (EXIT {filterRule})
            </button>
          {/if}
        </div>
      </div>
      <div class="dt-blotter-scroll">
        <table>
          <thead>
            <tr>
              <th>Bar time</th><th>Type</th><th>Symbol</th><th>Rule</th>
              <th class="num">Score</th><th>Weight pre → post</th><th class="num">Slippage</th><th>Execution</th>
            </tr>
          </thead>
          <tbody>
            {#each filtered.slice(0, blotterLimit) as d (d.id)}
              {@const meta = decisionMeta(d.decisionType)}
              {@const rule = d.decisionType === 'exit' || d.decisionType === 'external_close' ? exitRuleMeta(d.exitRule) : null}
              <tr
                class:selected={selectedDecision?.id === d.id}
                tabindex="0"
                on:click={() => (selectedDecision = d)}
                on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectedDecision = d; } }}>
                <td class="num">{fmtTime(d.barTime)}</td>
                <td><span class="dt-type" style={`color:${meta.color}`}>{meta.glyph}</span> {meta.label}</td>
                <td>{d.symbol ? d.symbol.replace('_', '-') : '— (whole book)'}</td>
                <td>
                  {#if rule && rule.id !== null}
                    <span class="dt-rule-chip" style={`border-color:${rule.color}`}>EXIT {rule.id} {rule.label}</span>
                  {:else}—{/if}
                </td>
                <td class="num">{primaryScore(d)}</td>
                <td class="num">{weightDelta(d)}</td>
                <td class="num">{firstSlippage(d)}</td>
                <td>{outcomeStatus(d)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
        {#if filtered.length > blotterLimit}
          <button type="button" class="btn btn-ghost btn-sm dt-more" on:click={() => (blotterLimit += 200)}>
            Show more ({filtered.length - blotterLimit} of {filtered.length} hidden)
          </button>
        {/if}
        {#if filtered.length === 0}
          <div class="empty-state"><p class="empty-state-description">No decisions match the current filters.</p></div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .dt-root { display: flex; flex-direction: column; gap: 0.85rem; }
  .dt-toolbar { display: flex; align-items: center; gap: 0.75rem; }
  .dt-live { font-size: 0.75rem; color: var(--success, #1aa260); }

  .dt-version {
    display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
    padding: 0.5rem 0.75rem; font-size: 0.75rem;
    border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  .dt-v-chip {
    font-size: 0.6rem; font-weight: 700; letter-spacing: 0.06em; padding: 0.12rem 0.4rem;
    border-radius: 3px; background: var(--surface-2, #1b1d23); color: var(--success, #1aa260);
    border: 1px solid var(--success, #1aa260);
  }
  .dt-v-id { font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .dt-v-ver { color: var(--text-secondary, #b5b6b8); }
  .dt-v-commit { color: var(--text-muted, #8a8b8d); font-variant-numeric: tabular-nums; }
  .dt-v-evidence { color: var(--text-muted, #8a8b8d); font-style: italic; }
  .dt-v-params { margin-left: auto; }
  .dt-v-params summary { cursor: pointer; color: var(--text-muted, #8a8b8d); }
  .dt-v-params pre {
    position: absolute; z-index: 30; margin-top: 0.3rem; padding: 0.6rem; max-height: 300px; max-width: 420px;
    overflow: auto; background: var(--surface-2, #101114); border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 6px; font-size: 0.7rem; color: var(--text-secondary, #b5b6b8);
  }

  .dt-tape-grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 0.85rem; align-items: start; }
  @media (max-width: 1100px) { .dt-tape-grid { grid-template-columns: 1fr; } }
  .dt-tape-main { min-width: 0; }

  .dt-panel-title { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  .dt-dist-total { font-weight: 400; color: var(--text-muted, #8a8b8d); font-size: 0.72rem; }
  .dt-dist { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .dt-dist-rows { display: flex; flex-direction: column; gap: 0.3rem; }
  .dt-dist-row {
    display: grid; grid-template-columns: 190px minmax(0, 1fr) 3rem; align-items: center; gap: 0.6rem;
    appearance: none; background: transparent; border: 1px solid transparent; border-radius: 4px;
    padding: 0.2rem 0.35rem; cursor: pointer; text-align: left; font-size: 0.75rem;
    color: var(--text-secondary, #b5b6b8);
  }
  .dt-dist-row:hover { background: var(--surface-1, rgba(255, 255, 255, 0.03)); }
  .dt-dist-row.selected { border-color: var(--accent, #3c78f0); }
  .dt-dist-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dt-dist-bar-track { height: 10px; background: var(--surface-2, #14151a); border-radius: 3px; overflow: hidden; }
  .dt-dist-bar { display: block; height: 100%; border-radius: 3px; min-width: 0; }
  .dt-dist-count { text-align: right; font-variant-numeric: tabular-nums; color: var(--text-primary, #e8e9ea); }

  .dt-blotter { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .dt-blotter-head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; }
  .dt-filters { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .dt-filters select {
    background: var(--surface-1, #14151a); color: var(--text-primary, #e8e9ea);
    border: 1px solid var(--border-subtle, #1e1f22); border-radius: 4px;
    font-size: 0.75rem; padding: 0.25rem 0.4rem;
  }
  .dt-blotter-scroll { max-height: 420px; overflow: auto; margin-top: 0.5rem; }
  .dt-blotter table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .dt-blotter th, .dt-blotter td { padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left; }
  .dt-blotter th { position: sticky; top: 0; background: var(--surface-1, #14151a); z-index: 1; }
  .dt-blotter th.num, .dt-blotter td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .dt-blotter tbody tr { cursor: pointer; }
  .dt-blotter tbody tr:hover { background: var(--surface-1, rgba(255, 255, 255, 0.03)); }
  .dt-blotter tbody tr.selected { background: var(--surface-2, rgba(60, 120, 240, 0.08)); outline: 1px solid var(--accent, #3c78f0); outline-offset: -1px; }
  .dt-type { font-size: 0.85rem; }
  .dt-rule-chip { border: 1px solid; border-radius: 4px; padding: 0.05rem 0.35rem; font-size: 0.7rem; }
  .dt-more { margin-top: 0.5rem; }
  .dt-empty { padding: 1rem; }
</style>
